import React, { useMemo, useState } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { PontoDiarioMarketing } from '../../../src/lib/marketing/agregacaoDiaria';
import { METRIC_CONFIG, METRIC_KEYS } from '../../../src/lib/marketing/metricConfig';
import type { MetricKey } from '../../../src/lib/marketing/metricConfig';
import MultiTooltip from '../../../src/components/marketing/MultiTooltip';

const fmtShort = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => v.toLocaleString('pt-BR');
const fmtDate = (d: string) => { const [, m, day] = d.split('-'); return `${day}/${m}`; };

interface Props {
  byDay: PontoDiarioMarketing[];
}

// Extraído de views/Marketing/MetaDashboard.tsx ("Análise de Métricas") pra
// ser reaproveitado também no Painel Executivo — mesmo componente, mesma
// lógica de agregação, sem duplicar nada (ver
// docs/superpowers/specs/2026-08-21-painel-executivo-design.md).
const AnaliseMetricasChart: React.FC<Props> = ({ byDay }) => {
  const [activeMetrics, setActiveMetrics] = useState<MetricKey[]>(
    METRIC_KEYS.filter(k => METRIC_CONFIG[k].defaultOn)
  );

  const multiChartData = useMemo(() => byDay.map(d => ({
    date: fmtDate(d.date), spend: d.spend, impressions: d.impressions,
    link_clicks: d.link_clicks, results_total: d.results_total,
    cost_per_result: d.cost_per_result,
  })), [byDay]);

  const toggleMetric = (key: MetricKey) =>
    setActiveMetrics(prev => prev.includes(key) ? (prev.length > 1 ? prev.filter(k => k !== key) : prev) : [...prev, key]);

  const hasLeftAxis  = activeMetrics.some(k => METRIC_CONFIG[k].axis === 'left');
  const hasRightAxis = activeMetrics.some(k => METRIC_CONFIG[k].axis === 'right');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Análise de Métricas</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Selecione as métricas para cruzar dados no período</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {METRIC_KEYS.map(key => {
            const cfg = METRIC_CONFIG[key];
            const on = activeMetrics.includes(key);
            return (
              <button key={key} onClick={() => toggleMetric(key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-all ${on ? 'text-white border-transparent' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-400'}`}
                style={on ? { background: cfg.color, borderColor: cfg.color } : {}}>
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: on ? 'rgba(255,255,255,0.7)' : cfg.color }} />
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={multiChartData} margin={{ top: 4, right: hasRightAxis ? 50 : 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
          {hasLeftAxis && <YAxis yAxisId="left" tickFormatter={v => fmtShort(v)} tick={{ fontSize: 11 }} tickLine={false} width={70} />}
          {hasRightAxis && <YAxis yAxisId="right" orientation="right" tickFormatter={v => fmtNum(v)} tick={{ fontSize: 11 }} tickLine={false} width={60} />}
          <Tooltip content={<MultiTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {activeMetrics.map(key => (
            <Line key={key} yAxisId={METRIC_CONFIG[key].axis} type="monotone" dataKey={key}
              name={METRIC_CONFIG[key].label} stroke={METRIC_CONFIG[key].color}
              strokeWidth={2} dot={false} connectNulls />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AnaliseMetricasChart;
